import { useEffect, useState } from 'react';
import {
  socialApi,
  summarizeCommandCenter,
  EMPTY_COMMAND_CENTER,
  type CommandCenterSummary,
} from '@/lib/social-api';
import { platformLabel } from '@/lib/social-platforms';
import { Button } from '@/components/ui/button';
import { PenSquare, Share2, CalendarDays, AlertTriangle, Link2 } from 'lucide-react';

export function CommandCenterStrip({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [data, setData] = useState<CommandCenterSummary | null>(null);

  useEffect(() => {
    void socialApi
      .commandCenter()
      .then((raw) => setData(summarizeCommandCenter(raw)))
      .catch(() => setData(EMPTY_COMMAND_CENTER));
  }, []);

  const d = data ?? EMPTY_COMMAND_CENTER;

  return (
    <section className="mb-8 w-full overflow-hidden rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Command Center</h2>
          <p className="mt-1 text-sm text-muted">
            Nächste Posts, Kanal-Status und schnelle Aktionen — Planen, Erstellen, Veröffentlichen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onNavigate('/composer')}>
            <PenSquare size={14} className="mr-1.5" /> Neuer Post
          </Button>
          <Button size="sm" variant="outline" onClick={() => onNavigate('/planner')}>
            <CalendarDays size={14} className="mr-1.5" /> Content-Planner
          </Button>
          <Button size="sm" variant="outline" onClick={() => onNavigate('/social')}>
            <Share2 size={14} className="mr-1.5" /> Social Hub
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MiniStat label="Aktive Kanäle" value={d.accounts_active} onClick={() => onNavigate('/social/accounts')} />
        <MiniStat label="Geplant (7 Tage)" value={d.scheduled_next_7_days} onClick={() => onNavigate('/social/queue')} />
        <MiniStat
          label="Fehler"
          value={d.failed_targets}
          tone={d.failed_targets > 0 ? 'danger' : 'default'}
          onClick={() => onNavigate('/social/queue')}
        />
        <MiniStat
          label="Tokens abgelaufen"
          value={d.accounts_expired}
          tone={d.accounts_expired > 0 ? 'warn' : 'default'}
          onClick={() => onNavigate('/social/accounts')}
        />
      </div>

      {(d.accounts_expired > 0 || d.failed_targets > 0) && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Es gibt Handlungsbedarf bei Kanälen oder der Warteschlange.{' '}
            <button type="button" className="font-semibold underline" onClick={() => onNavigate('/social')}>
              Zum Social Hub
            </button>
          </span>
        </div>
      )}

      {d.upcoming.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium text-muted">Nächste 7 Tage</h3>
          <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
            {d.upcoming.slice(0, 5).map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm hover:bg-background dark:hover:bg-card"
                  onClick={() => onNavigate(`/composer/${u.post_id}`)}
                >
                  <span className="truncate text-foreground">{u.preview}</span>
                  <span className="shrink-0 text-xs text-muted">
                    {platformLabel(u.platform)} · {new Date(u.scheduled_at).toLocaleString('de-CH')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.accounts_active === 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted">
          <Link2 size={16} />
          Noch keine Social-Konten verbunden —{' '}
          <button
            type="button"
            className="font-medium text-foreground underline"
            onClick={() => onNavigate('/social/accounts')}
          >
            jetzt verbinden
          </button>
        </div>
      )}
    </section>
  );
}

function MiniStat({
  label,
  value,
  onClick,
  tone = 'default',
}: {
  label: string;
  value: number;
  onClick: () => void;
  tone?: 'default' | 'danger' | 'warn';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-border bg-background px-4 py-3 text-left transition hover:border-border hover:bg-card-hover dark:bg-card dark:hover:bg-card-hover/80"
    >
      <p className="text-[11px] font-medium text-muted">{label}</p>
      <p
        className={
          tone === 'danger'
            ? 'mt-1 text-2xl font-semibold text-danger'
            : tone === 'warn'
              ? 'mt-1 text-2xl font-semibold text-warning'
              : 'mt-1 text-2xl font-semibold text-foreground'
        }
      >
        {value}
      </p>
    </button>
  );
}
